import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { ChevronDown, HelpCircle, Sparkles } from "lucide-react";

const FAQS = [
    {
        q: { en: "How do I book a shoot with Gloom Ent.?", fr: "Comment réserver un tournage avec Gloom Ent. ?" },
        a: {
            en: "Use the estimator on the pricing page for an indicative quote, then pay a deposit (50%) to lock your date. We then contact you within 48h to plan the brief, moodboard and shoot.",
            fr: "Utilisez l'estimateur sur la page tarifs pour un devis indicatif, puis payez un acompte (50%) pour bloquer votre date. Nous vous contactons ensuite sous 48h pour caler le brief, le moodboard et le tournage.",
        },
    },
    {
        q: { en: "What do the image exploitation rights (+200%) cover?", fr: "Que couvrent les droits d'exploitation des images (+200%) ?" },
        a: {
            en: "The base rate includes a standard licence to broadcast the final clip on your own platforms. The full rights transfer (+200%) gives you the raw footage, the right to re-edit with another professional, to transfer to a label/agency, and to exploit the footage commercially. Without it, the footage stays under standard licence.",
            fr: "Le tarif de base inclut une licence standard pour diffuser le clip final sur vos propres plateformes. La cession intégrale (+200%) vous donne les rushes, le droit de remonter avec un autre professionnel, de transmettre à un label/agence, et d'exploiter les images commercialement. Sans elle, les images restent sous licence standard.",
        },
    },
    {
        q: { en: "I am a loyal client — how do I access the site before the public launch?", fr: "Je suis un client fidèle — comment accéder au site avant le lancement public ?" },
        a: {
            en: "You received the private link directly. Create your account (MON ESPACE → INSCRIPTION), explore the site and tell us if anything needs adjusting. This is a soft-launch phase — your feedback shapes the final version before we open it widely.",
            fr: "Vous avez reçu le lien privé directement. Créez votre compte (MON ESPACE → INSCRIPTION), explorez le site et dites-nous si quelque chose doit être ajusté. C'est une phase de lancement en douceur — vos retours façonnent la version finale avant l'ouverture large.",
        },
    },
    {
        q: { en: "I am an artist produced by Gloom — how do I claim my page?", fr: "Je suis un artiste produit par Gloom — comment réclamer ma page ?" },
        a: {
            en: "Go to MON ESPACE → INSCRIPTION → 'I AM AN ARTIST', answer your profile questions and enter the starter code you were given. You will then set your own personal code. Once connected, you can edit your bio, links and complete your press kit.",
            fr: "Allez dans MON ESPACE → INSCRIPTION → « JE SUIS ARTISTE », répondez aux questions de votre profil et entrez le code de départ qui vous a été remis. Vous définirez ensuite votre code personnel. Une fois connecté, vous pouvez modifier votre bio, vos liens et compléter votre press kit.",
        },
    },
    {
        q: { en: "How does GLM Radio work?", fr: "Comment fonctionne GLM Radio ?" },
        a: {
            en: "GLM Radio plays Swiss artists — clips and tracks curated by Gloom. Click any clip to watch it; the sound keeps playing in the background if you browse elsewhere. You can also submit your own track for validation.",
            fr: "GLM Radio diffuse des artistes suisses — clips et sons sélectionnés par Gloom. Cliquez sur un clip pour le regarder ; le son continue en arrière-plan si vous naviguez ailleurs. Vous pouvez aussi proposer votre propre morceau pour validation.",
        },
    },
    {
        q: { en: "What payment methods do you accept?", fr: "Quels moyens de paiement acceptez-vous ?" },
        a: {
            en: "Deposits are paid securely by card via Stripe. The balance is paid before the shoot day, as agreed in your quote.",
            fr: "Les acomptes se paient de façon sécurisée par carte via Stripe. Le solde est réglé avant le jour du tournage, selon votre devis.",
        },
    },
    {
        q: { en: "Can I get a refund if I cancel?", fr: "Puis-je être remboursé en cas d'annulation ?" },
        a: {
            en: "The deposit locks your date and covers preparation. Contact us as early as possible — we handle each situation case by case and can usually reschedule.",
            fr: "L'acompte bloque votre date et couvre la préparation. Contactez-nous au plus tôt — nous traitons chaque situation au cas par cas et pouvons généralement reprogrammer.",
        },
    },
    {
        q: { en: "Who is 6xo and how do I book him?", fr: "Qui est 6xo et comment le réserver ?" },
        a: {
            en: "6xo is a Geneva-based Swiss photographer and director, Gloom's assistant. His services are available on request for weekends — use the 'BOOK WITH 6XO' button on the Artists page.",
            fr: "6xo est un photographe et réalisateur suisse genevois, assistant de Gloom. Ses services sont disponibles sur demande pour les weekends — utilisez le bouton « RÉSERVER AVEC 6XO » sur la page Artistes.",
        },
    },
];

export default function FaqPage() {
    const { lang } = useLang();
    const [open, setOpen] = useState(0);

    return (
        <section data-testid="faq-page" className="relative min-h-screen bg-ink px-5 pb-24 pt-32 text-cream md:px-10">
            <div className="pointer-events-none absolute inset-0 opacity-20" style={{ background: "radial-gradient(50% 40% at 80% 0%, rgba(138,43,226,0.22) 0%, transparent 70%)" }} />
            <div className="relative mx-auto max-w-[900px]">
                <SectionLabel en={tx(lang, { en: "11 — FAQ", fr: "11 — FAQ" })} fr={tx(lang, { en: "11 — FAQ", fr: "11 — FAQ" })} />
                <FadeUp delay={0.05}>
                    <h1 className="mt-8 font-display text-[clamp(2.4rem,7vw,5.5rem)] font-extrabold uppercase leading-none tracking-[-0.02em]">
                        {lang === "fr" ? "QUESTIONS FRÉQUENTES" : "FREQUENTLY ASKED"}
                        <span className="text-violet">.</span>
                    </h1>
                    <p className="mt-4 flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-gold">
                        <Sparkles size={13} /> {lang === "fr" ? "LANCEMENT EN DOUCEUR — POUR NOS CLIENTS FIDÈLES" : "SOFT LAUNCH — FOR OUR LOYAL CLIENTS"}
                    </p>
                </FadeUp>

                <div className="mt-12 space-y-3">
                    {FAQS.map((f, i) => (
                        <FadeUp key={i} delay={0.05 + i * 0.04}>
                            <div className={`border transition-colors ${open === i ? "border-violet/50 bg-violet/[0.05]" : "border-white/10"}`}>
                                <button
                                    data-testid={`faq-q-${i}`}
                                    onClick={() => setOpen(open === i ? -1 : i)}
                                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                                >
                                    <span className="flex items-start gap-3">
                                        <HelpCircle size={15} className="mt-0.5 shrink-0 text-gold" />
                                        <span className="font-mono text-[11px] leading-relaxed tracking-[0.15em] text-cream">{tx(lang, f.q)}</span>
                                    </span>
                                    <motion.span animate={{ rotate: open === i ? 180 : 0 }} transition={{ duration: 0.3 }}>
                                        <ChevronDown size={15} className="shrink-0 text-cream/50" />
                                    </motion.span>
                                </button>
                                <AnimatePresence initial={false}>
                                    {open === i && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                            className="overflow-hidden"
                                        >
                                            <p data-testid={`faq-a-${i}`} className="border-t border-white/10 px-6 py-5 pl-14 font-mono text-[10px] leading-relaxed tracking-[0.15em] text-cream/60">
                                                {tx(lang, f.a)}
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </FadeUp>
                    ))}
                </div>

                <FadeUp delay={0.2} className="mt-14 text-center">
                    <p className="font-mono text-[9px] tracking-[0.3em] text-cream/40">
                        {lang === "fr" ? "UNE AUTRE QUESTION ?" : "ANOTHER QUESTION?"}
                    </p>
                    <button
                        data-testid="faq-contact-btn"
                        onClick={() => window.__goPage?.("contact")}
                        className="mt-4 border border-gold bg-gold/10 px-8 py-3.5 font-mono text-[10px] tracking-[0.3em] text-gold transition-all hover:bg-gold hover:text-ink"
                    >
                        {lang === "fr" ? "NOUS CONTACTER" : "CONTACT US"}
                    </button>
                </FadeUp>
            </div>
        </section>
    );
}
